// packages/shared/src/schemas/product.ts
// Zod schemas for products and variants — used by both API and Web
import { z } from 'zod'
import { CategorySlugSchema } from './category.js'

/** @deprecated Use CategorySlugSchema — categories are now dynamic from D1 */
export const ProductCategoryEnum = CategorySlugSchema

export const AttributeTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  values: z.array(z.string()),
  unit: z.string().nullable(),
  sortOrder: z.number(),
})

export const VariantAttributeSchema = z.object({
  id: z.string(),
  variantId: z.string(),
  attrTypeId: z.string(),
  attrTypeName: z.string().optional(),  // joined
  attrValue: z.string(),
})

export const ProductVariantSchema = z.object({
  id: z.string(),
  productId: z.string(),
  sku: z.string(),
  priceOverride: z.number().nullable(),
  compareAtPrice: z.number().optional().nullable(),
  stockQty: z.number().int().min(0),
  inStock: z.boolean().optional(),
  lowStockThreshold: z.number().int().min(0),
  isActive: z.boolean(),
  attributes: z.array(VariantAttributeSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const ProductImageSchema = z.object({
  id: z.string(),
  productId: z.string(),
  url: z.string().url(),
  altText: z.string().nullable(),
  sortOrder: z.number(),
})

export const ProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: CategorySlugSchema,
  basePrice: z.number().min(0),
  compareAtPrice: z.number().optional().nullable(),
  sku: z.string(),
  stockQty: z.number().int().min(0).optional(),
  isActive: z.boolean(),
  isFeatured: z.boolean(),
  lowStockThreshold: z.number().int().min(0),
  heroImageUrl: z.string().url().nullable(),
  totalStock: z.number().int().min(0).optional(),
  inStock: z.boolean().optional(),
  images: z.array(ProductImageSchema).optional(),
  variants: z.array(ProductVariantSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

// Input schemas (for create/update)
export const CreateProductInput = z.object({
  name: z.string().min(1, 'Name is required').max(200).trim(),
  description: z.string().max(2000).trim().optional(),
  category: CategorySlugSchema,
  basePrice: z.number().min(0, 'Price must be non-negative'),
  compareAtPrice: z.number().min(0).optional().nullable(),
  sku: z.string().max(50).trim().toUpperCase().optional(),
  stockQty: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  isFeatured: z.boolean().default(false),
  lowStockThreshold: z.number().int().min(0).default(5),
  heroImageUrl: z.string().optional().nullable(),
})

export const UpdateProductInput = CreateProductInput.partial()

export const CreateVariantInput = z.object({
  productId: z.string(),
  sku: z.string().max(50).trim().toUpperCase().optional(),
  priceOverride: z.number().min(0).nullable().optional(),
  compareAtPrice: z.number().min(0).optional().nullable(),
  stockQty: z.number().int().min(0).default(0),
  lowStockThreshold: z.number().int().min(0).default(5),
  attributes: z.array(z.object({
    attrTypeId: z.string(),
    attrValue: z.string().min(1),
  })).min(1, 'At least one attribute required'),
})

export const UpdateVariantInput = CreateVariantInput.omit({ productId: true, attributes: true }).partial().extend({
  isActive: z.boolean().optional(),
})

export const ProductListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(20),
  search: z.string().trim().optional(),
  category: CategorySlugSchema.optional(),
  isActive: z.coerce.boolean().optional(),
  isFeatured: z.coerce.boolean().optional(),
  sortBy: z.enum(['name', 'base_price', 'created_at', 'updated_at']).default('created_at'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
})

export type Product = z.infer<typeof ProductSchema>
export type ProductVariant = z.infer<typeof ProductVariantSchema>
export type CreateProductInput = z.infer<typeof CreateProductInput>
export type UpdateProductInput = z.infer<typeof UpdateProductInput>
export type CreateVariantInput = z.infer<typeof CreateVariantInput>
